'use client'

import type { BigIntish, Fetchable } from '@morpho-org/blue-sdk'
import type { InterfaceInputToken } from '@morpho-org/dapp-core'
import type { HTMLAttributes, ReactNode } from 'react'

import { isDefined } from '@morpho-org/morpho-ts'
import { cn } from '@morpho-org/ui-v6/utils'
import { Badge, Tooltip, TooltipContent, TooltipTrigger, TruncatedTooltip } from '@repo/ui'
import { forwardRef } from 'react'

import { getMarketNameParts } from '../../utils/getMarketName'
import { Avatar } from './Avatar'
import { InfoTooltip } from './InfoTooltip'

export interface MarketIdentityProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Loan asset — the market's headline identity */
  loanAsset: Fetchable<InterfaceInputToken>
  /** Collateral asset. Omit for idle-liquidity markets. */
  collateralAsset?: Fetchable<InterfaceInputToken>
  /** Liquidation LTV (18 decimals) */
  lltv?: BigIntish | null
  /** Maximum length for the symbols before truncation */
  maxLength?: number
  /** Tailwind text classes applied to both symbols */
  textClassName?: string
  /**
   * Append an `Idle Liquidity` badge on idle markets. Off by default — it is a
   * table-cell affordance, too heavy for inline links and card headers.
   */
  showIdleBadge?: boolean
  /** Replaces the default idle-liquidity tooltip. Only used when `showIdleBadge` is set. */
  renderIdleTooltip?: ReactNode
  /**
   * Let symbols shrink and ellipsize inside constrained layouts. Set to false in
   * auto-layout table cells so the symbol keeps its intrinsic width.
   */
  truncateSymbols?: boolean
}

/**
 * Loan-asset-first market identifier (VRM-5356, aligned with FRM's MKT-1070).
 *
 * Renders the agreed `Settlement Currency | Loan Backing` shorthand — loan asset,
 * a divider, then the trust assumption (collateral + LLTV) — so a market never
 * reads as a base/quote trading pair. VRM markets carry no maturity, so nothing
 * follows the backing.
 *
 * Idle-liquidity markets have no collateral: a single loan avatar, the loan symbol,
 * and an `Idle Liquidity` badge in place of the backing.
 *
 * Render it inside a link or focusable row: a clipped symbol leaves the tab order, so the
 * parent must carry the full value.
 *
 * @example
 * ```tsx
 * <MarketIdentity loanAsset={usdc} collateralAsset={cbBTC} lltv={860000000000000000n} />
 * // ◉USDC │ ◉cbBTC [86%]
 * ```
 */
const MarketIdentity = forwardRef<HTMLDivElement, MarketIdentityProps>(
  (
    {
      loanAsset,
      collateralAsset,
      lltv,
      maxLength,
      textClassName = 'text-2xs',
      showIdleBadge = false,
      renderIdleTooltip,
      truncateSymbols = true,
      className,
      ...props
    },
    ref
  ) => {
    // Idle is a property of the market, not of the fetch: a market with no collateral is
    // idle whether or not the loan asset has landed yet.
    const isIdleMarket = !isDefined(collateralAsset)

    const nameParts = isDefined(loanAsset)
      ? getMarketNameParts({ loanAsset, collateralAsset, lltv, maxLength })
      : null

    const symbolClassName = cn(
      'text-foreground leading-normal',
      truncateSymbols ? 'min-w-0 truncate' : 'whitespace-nowrap',
      textClassName
    )

    return (
      <div ref={ref} className={cn('gap-xs flex min-w-0 items-center', className)} {...props}>
        <span className={cn('gap-xs inline-flex items-center', truncateSymbols && 'min-w-0')}>
          <Avatar aria-hidden src={loanAsset?.logoURI} size="small" />
          <MarketSymbol text={nameParts?.loanSymbol} className={symbolClassName} />
        </span>

        {isIdleMarket ? (
          showIdleBadge && (
            <>
              <Badge variant="tertiary" size="sm">
                Idle Liquidity
              </Badge>
              {renderIdleTooltip ?? (
                <InfoTooltip
                  tooltipContent={
                    <>
                      Total deposits that are not being lent out by the curator. Idle liquidity can
                      not be borrowed or generate yield.
                    </>
                  }
                  className="text-muted-foreground cursor-help"
                />
              )}
            </>
          )
        ) : (
          <>
            {/* Not a `|` character: a rule, so the pair never reads as base/quote. The
                rule itself is decorative, so the relationship it conveys visually is
                spelled out for screen readers instead of collapsing to "USDCcbBTC". */}
            <span aria-hidden className="bg-muted-foreground h-sm w-px shrink-0 rounded-full" />
            <span className="sr-only">backed by</span>

            <span className={cn('gap-xs inline-flex items-center', truncateSymbols && 'min-w-0')}>
              <Avatar aria-hidden src={collateralAsset?.logoURI} size="small" />
              <MarketSymbol text={nameParts?.collateralSymbol} className={symbolClassName} />

              {nameParts?.formattedLltv != null && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <Badge variant="tertiary" size="sm">
                        {nameParts.formattedLltv}
                        {/* A bare percentage means nothing read aloud. */}
                        <span className="sr-only"> LLTV</span>
                      </Badge>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>Liquidation Loan-To-Value (LLTV).</TooltipContent>
                </Tooltip>
              )}
            </span>
          </>
        )}
      </div>
    )
  }
)

/**
 * A market symbol that reveals its full value on hover once CSS clips it. Kept out of the
 * tab order: market identities sit inside row and inline links, whose text already carries
 * the full symbol.
 */
const MarketSymbol = ({ text, className }: { text?: string | null; className: string }) => {
  return (
    <TruncatedTooltip text={text ?? ''}>
      {(symbolRef, isTruncated) => (
        <span ref={symbolRef} tabIndex={isTruncated ? -1 : undefined} className={className}>
          {text}
        </span>
      )}
    </TruncatedTooltip>
  )
}

MarketIdentity.displayName = 'MarketIdentity'

export { MarketIdentity }
